// Iconos propios, dibujados para este proyecto: representan los mismos componentes que muestra OpenRocket
// pero no reutilizan su ilustración (la aplicación es una implementación independiente).
import type { ReactNode } from 'react';

export type IconName =
  | 'stage'
  | 'boosters'
  | 'pods'
  | 'nose-cone'
  | 'body-tube'
  | 'transition'
  | 'fin-trapezoid'
  | 'fin-elliptical'
  | 'fin-freeform'
  | 'tube-fins'
  | 'rail-button'
  | 'launch-lug'
  | 'inner-tube'
  | 'coupler'
  | 'centering-ring'
  | 'bulkhead'
  | 'engine-block'
  | 'motor-mount'
  | 'parachute'
  | 'shock-cord'
  | 'mass'
  | 'zoom-in'
  | 'zoom-out'
  | 'tool-new'
  | 'tool-rename'
  | 'tool-delete'
  | 'tool-copy'
  | 'tool-edit'
  | 'tool-run'
  | 'tool-plot'
  | 'status-none'
  | 'status-ok'
  | 'status-stale'
  | 'status-bad';

type ShapeName = Exclude<IconName, 'zoom-in' | 'zoom-out' | ToolName>;
type ToolName = Extract<IconName, `tool-${string}` | `status-${string}`>;

/** Iconos pequeños (24×24) de los botones de las barras y de los estados de las listas. */
const TOOLS: Record<ToolName, ReactNode> = {
  'tool-new': (
    <>
      <path className="fill" d="M6 3 H14 L19 8 V21 H6Z" />
      <path d="M14 3 V8 H19" />
      <path d="M12.5 12 V18 M9.5 15 H15.5" className="tone-ok" />
    </>
  ),
  'tool-rename': (
    <>
      <rect className="fill" x="3" y="8" width="18" height="9" rx="1.5" />
      <path d="M12 5 V20 M9.5 5 H14.5 M9.5 20 H14.5" />
    </>
  ),
  'tool-delete': <path d="M6 6 L18 18 M18 6 L6 18" strokeWidth="3" className="tone-bad" />,
  'tool-copy': (
    <>
      <rect className="fill" x="4" y="4" width="11" height="13" rx="1.5" />
      <rect className="fill" x="9" y="8" width="11" height="13" rx="1.5" />
    </>
  ),
  'tool-edit': (
    <>
      <path className="fill" d="M5 19 L6 14 L16 4 L20 8 L10 18Z" />
      <path d="M14 6 L18 10" />
    </>
  ),
  'tool-run': <path className="fill tone-ok" d="M7 4 L19 12 L7 20Z" />,
  'tool-plot': (
    <>
      <path d="M4 4 V20 H21" />
      <path d="M7 16 L11 10 L14 13 L19 6" className="tone-info" strokeWidth="2" />
    </>
  ),
  'status-none': <circle className="fill tone-warn" cx="12" cy="12" r="6.5" />,
  'status-ok': (
    <>
      <circle className="fill tone-info" cx="9" cy="12" r="6" />
      <path d="M14 13 L17 16 L22 8" className="tone-ok" strokeWidth="2.4" />
    </>
  ),
  'status-stale': (
    <>
      <circle className="fill tone-info" cx="9" cy="12" r="6" />
      <path d="M18 6 V13 M18 17 V18" className="tone-warn" strokeWidth="2.6" />
    </>
  ),
  'status-bad': <path d="M12 5 V14 M12 18.5 V19" className="tone-bad" strokeWidth="3" />,
};

const SHAPES: Record<ShapeName, ReactNode> = {
  stage: (
    <>
      <path className="fill" d="M24 3 C30 8 31 14 31 20 L31 27 L17 27 L17 20 C17 14 18 8 24 3Z" />
      <path className="fill" d="M17 17 L8 28 L17 27Z M31 17 L40 28 L31 27Z" />
      <path d="M21 27 L21 30 M27 27 L27 30" />
    </>
  ),
  boosters: (
    <>
      <path className="fill" d="M8 10 L30 10 C36 10 40 12 42 14 C40 16 36 18 30 18 L8 18Z" />
      <path className="fill" d="M8 20 L30 20 C36 20 40 22 42 24 C40 26 36 28 30 28 L8 28Z" />
      <path d="M8 10 L4 7 M8 18 L4 21 M8 20 L4 17 M8 28 L4 31" />
    </>
  ),
  pods: (
    <>
      <circle className="fill" cx="24" cy="16" r="3.5" />
      <path d="M10 8 v4 M8 10 h4 M38 8 v4 M36 10 h4 M10 22 v4 M8 24 h4 M38 22 v4 M36 24 h4" />
    </>
  ),
  'nose-cone': (
    <>
      <path className="fill" d="M5 16 C14 8 26 8 36 11 L36 21 C26 24 14 24 5 16Z" />
      <rect className="fill" x="36" y="11" width="7" height="10" rx="1" />
    </>
  ),
  'body-tube': (
    <>
      <rect className="fill" x="6" y="10" width="36" height="12" rx="1.5" />
      <path d="M6 13 H42" />
    </>
  ),
  transition: (
    <>
      <path className="fill" d="M6 6 L38 12 L38 20 L6 26Z" />
      <rect className="fill" x="38" y="12" width="5" height="8" />
    </>
  ),
  'fin-trapezoid': (
    <>
      <path className="fill" d="M6 25 L42 25 L36 14 L22 14Z" />
      <path d="M6 25 H42" />
    </>
  ),
  'fin-elliptical': (
    <>
      <path className="fill" d="M8 25 C8 14 18 8 28 8 C36 8 40 16 40 25Z" />
    </>
  ),
  'fin-freeform': (
    <>
      <path className="fill" d="M6 25 L42 25 C40 18 34 14 30 6 C26 14 20 16 14 20Z" />
    </>
  ),
  'tube-fins': (
    <>
      <rect className="fill" x="8" y="7" width="32" height="9" rx="3" />
      <rect className="fill" x="8" y="16" width="32" height="9" rx="3" />
      <path d="M14 7 V25 M34 7 V25" />
    </>
  ),
  'rail-button': (
    <>
      <path className="fill" d="M16 26 H32 V22 H16Z" />
      <path className="fill" d="M21 22 V17 H27 V22Z" />
      <rect className="fill" x="18" y="11" width="12" height="6" rx="2" />
    </>
  ),
  'launch-lug': (
    <>
      <path className="fill" d="M10 25 H38 V20 H10Z" />
      <rect className="fill" x="18" y="12" width="12" height="8" rx="3" />
      <ellipse cx="18" cy="16" rx="1.5" ry="3" />
    </>
  ),
  'inner-tube': (
    <>
      <rect className="fill" x="6" y="9" width="36" height="14" rx="2" />
      <rect x="9" y="12" width="30" height="8" rx="1" />
    </>
  ),
  coupler: (
    <>
      <rect className="fill" x="6" y="10" width="36" height="12" rx="1.5" />
      <path d="M16 10 V22 M32 10 V22" />
    </>
  ),
  'centering-ring': (
    <>
      <rect className="fill" x="8" y="6" width="6" height="20" rx="1.5" />
      <path d="M14 11 H40 M14 21 H40" />
      <ellipse cx="40" cy="16" rx="1.6" ry="5" />
    </>
  ),
  bulkhead: (
    <>
      <rect className="fill" x="19" y="5" width="10" height="22" rx="2" />
      <path d="M24 5 V27" />
    </>
  ),
  'engine-block': (
    <>
      <rect className="fill" x="6" y="9" width="36" height="14" rx="2" />
      <rect className="fill" x="30" y="12" width="6" height="8" />
    </>
  ),
  'motor-mount': (
    <>
      <rect className="fill" x="6" y="10" width="28" height="12" rx="1.5" />
      <path className="fill" d="M34 12 L42 9 L42 23 L34 20Z" />
    </>
  ),
  parachute: (
    <>
      <path className="fill" d="M6 17 C6 6 42 6 42 17 C38 14 34 14 31 17 C28 14 20 14 17 17 C14 14 10 14 6 17Z" />
      <path d="M6 17 L24 29 M17 17 L24 29 M31 17 L24 29 M42 17 L24 29" />
    </>
  ),
  'shock-cord': (
    <>
      <path d="M5 22 C12 6 18 28 24 16 C30 4 36 26 43 10" />
      <circle className="fill" cx="5" cy="22" r="2" />
      <circle className="fill" cx="43" cy="10" r="2" />
    </>
  ),
  mass: (
    <>
      <path className="fill" d="M14 25 L18 10 H30 L34 25Z" />
      <circle cx="24" cy="7" r="2.5" />
    </>
  ),
};

interface Props {
  readonly name: IconName;
  readonly size?: number;
}

/** Icono decorativo: el botón que lo contiene ya lleva el nombre. */
export function Icon({ name, size }: Props): JSX.Element {
  if (name in TOOLS) {
    return (
      <svg className="ico ico--tool" viewBox="0 0 24 24" width={size ?? 18} height={size ?? 18} aria-hidden="true" focusable="false">
        {TOOLS[name as ToolName]}
      </svg>
    );
  }
  if (name === 'zoom-in' || name === 'zoom-out') {
    return (
      <svg className="ico ico--lens" viewBox="0 0 24 24" width={size ?? 18} height={size ?? 18} aria-hidden="true" focusable="false">
        <circle className="fill" cx="10" cy="10" r="6.5" />
        <path d="M15 15 L21 21" strokeWidth="2.4" />
        <path d={name === 'zoom-in' ? 'M7 10 H13 M10 7 V13' : 'M7 10 H13'} />
      </svg>
    );
  }
  return (
    <svg className="ico" viewBox="0 0 48 32" width={size ?? 48} height={((size ?? 48) * 32) / 48} aria-hidden="true" focusable="false">
      {SHAPES[name as ShapeName]}
    </svg>
  );
}
